/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MetricsModule */

import {
    cloneDeep,
    filter,
    find,
    findLastIndex,
    isFinite,
    isNaN,
    isObject,
    isUndefined,
} from 'lodash';

import moment, { Moment } from 'moment';

import {
    IMetricsData,
    IMetricsDataHeader,
    IMetricsDataSeries,
    MetricUnits,
} from 'generated-types';

import { StringService } from 'string-service';
import { Base } from 'ajs/modules/data-model/factories/base.factory';
import { STRING_SERVICE_TOKEN } from 'ajs/modules/core/core.tokens';

import { MetricsDisplayValueType } from 'ng/root-store/user-preferences/user-preferences.state';

/**
 * Some property highlights for type IMetricsDataHeader {@link IMetricsDataHeader}
 * @property entity_uuid - Actual item (VS/Pool/SE) Id this series is about.
 * @property metrics_sum_agg_invalid - When true can't calculate sum of values over time.
 * @property metric_description - For series of a string type this can have a comma
 *     separated list of all possible values. Oreder does matter. When those are not known in
 *     advance this string can have no value.
 */
interface ISeriesHeader extends IMetricsDataHeader {
    title?: string;
    // somtimes entity_ref will be present instead of the uuid (by Webapp swapping)
    entity_ref?: string;
    // sometimes pool_ref will be present instead of the uuid (by Webapp swapping)
    pool_ref?: string;
    // a UI property; the list version of comma separated metric_description
    stringValuesList?: string[];
}

/**
 * @property timestamp - UNIX timestamp (in ms).
 * @property value - Series value.
 * @property noData - This flag is set to true for dummy values when back end
 *     has no data.
 */
interface ISeriesDataPoint {
    timestamp?: number;
    value?: number | string;
    noData?: boolean;
}

interface ISeriesStats {
    max?: number;
    sum?: number;
    avg?: number;
    maxTimestamp?: number;
    current?: ISeriesDataPoint; // Deprecated. Reference to the most recent data point.
}

/**
 * @property step - Difference in seconds between two data points.
 * @property limit - How many points do we need for the series.
 */
interface ISeriesParams {
    step?: number;
    limit?: number;
}

/**
 * The core data of the series that are of use by the caller.
 */
interface ISeriesData {
    values?: ISeriesDataPoint[];
    header?: ISeriesHeader;
    valuesHash?: Record<number, ISeriesDataPoint>;
    statistics?: ISeriesStats;
}

type TMetricsDataApiResponse = IMetricsDataSeries | IMetricsDataSeries[];

/**
 * Time frame object with a start and an end point.
 */
export interface ITimeFrame {
    start?: Moment,
    end?: Moment,
}

export const SERIES_TOKEN = 'Series';

/**
 * @description
 *
 *     Series class. Keeps series values, anomalies, dominant factors (when applicable),
 *     header and statistics.
 *
 *     Usually used witin {@link Metric} instance.
 *
 * @author Alex Malitsky, Zhiqian Liu
 */
export class Series extends Base {
    /**
     * Values update event name.
     */
    public static VALUES_UPDATE_EVENT = 'valuesUpdate';

    /**
     * Anomalies update event name.
     */
    public static ANOMALIES_UPDATE_EVENT = 'anomaliesUpdate';

    public readonly stringService: StringService;

    /**
     * Actual series ID, such as se_if.avg_bandwidth.
     */
    public seriesId: string;

    /**
     * Series ID within Metric.seriesHash_.
     * Series are usually stored in Metric.seriesHash_, hence need to have a unique id
     * even when seriesId, itemId and objId are the same for different series within one
     * Metric instance. Rare case but still supported.
     */
    public id: string;

    /**
     * Human readable series name.
     */
    public title?: string;

    /**
     * Ref of the item the series is bound to.
     */
    public itemRef?: string;

    /**
     * ID of the item the series is bound to.
     * When set, is used for API response filtering.
     */
    public itemId?: string;

    /**
     * Type of the item the series is bound to.
     */
    public itemType?: string;

    /**
     * Some series are fetched with not just itemId but objId/serverId as well.
     * When set, is used for response filtering.
     */
    public objId?: string;

    /**
     * List of dimensions the Series keeps data about.
     */
    public dimensions?: string[];

    /**
     * Dimension ID when applicable.
     */
    public dimensionId?: string;

    /**
     * Ordered by the timestamp list of data points. Length of the list is defined by
     * limit parameter.
     */
    public values: ISeriesDataPoint[] = [];

    /**
     * Hash of data points by UNIX timestamps.
     */
    public valuesHash: Record<number, ISeriesDataPoint> = {};

    /**
     * Ordered by timestamp list of anomalies.
     */
    public anomalies: ISeriesDataPoint[] = [];

    /**
     * Total number of anomalies for the Series timeframe.
     */
    public anomaliesQ = 0;

    /**
     * Data statistics about the series. eg. avg, sum.
     */
    public statistics: ISeriesStats = {};

    /**
     * Series header copied from the backend response with simple UI properties.
     */
    public header: ISeriesHeader = {};

    /**
     * Dominant contributors times.
     */
    public dominators: string[] = [];

    /**
     * dimension_aggregation parameter value. Set for aggregated series {@link AggSeries}
     * only.
     */
    public aggregation: string;

    protected params: ISeriesParams = {};

    /**
     * Sometimes having aggregation param doesn't mean Series is gonna be aggregated. For
     * these cases we can pass such argument as an override.
     */
    protected isAggregatedSeries: boolean;

    /**
     * Hash of data points by timestamps.
     */
    protected anomaliesHash: Record<number, ISeriesDataPoint> = {};

    /**
     * CSS class of the color that the series is rendered with in the chart.
     */
    private colorClassName?: string;

    constructor(args: any = {}) {
        super(args);

        this.stringService = this.getAjsDependency_(STRING_SERVICE_TOKEN);

        this.seriesId = args.seriesId || args.id; // actual series name/id

        if (!this.seriesId) {
            console.warn('Can not create a series wo series id', this);
        }

        this.itemRef = args.itemRef || '';
        this.itemId = args.itemId || this.stringService.slug(this.itemRef) || '';
        this.itemType = args.itemType || '';
        this.objId = args.objId || '';
        this.dimensions = Array.isArray(args.dimensions) ? args.dimensions.concat() : [];
        this.dimensionId = args.dimensionId || '';
        this.id = args.id || this.getId();

        const NamesHelper = this.getAjsDependency_('NamesHelper');

        this.title = args.title || NamesHelper.getTitle(this.getSeriesId(), true);
        this.colorClassName = args.colorClassName ||
            NamesHelper.getColorClass(this.getSeriesId());

        this.aggregation = args.aggregation || '';

        this.isAggregatedSeries = false;

        if (this.aggregation) {
            this.isAggregatedSeries = isUndefined(args.isAggregated) || !!args.isAggregated;
        }

        // can create series with data
        if (isObject(args.data)) {
            const {
                step,
                limit,
                seriesData: data,
                anomalyData,
            } = args.data;

            this.process(data, anomalyData, step, limit);
        }
    }

    /**
     * Custom type checked for series object provided by the back end.
     */
    public static isSeriesDataObj(sData: Record<any, any>): boolean {
        return isObject(sData) && 'header' in sData && 'data' in sData;
    }

    /**
     * Data transformer for series data point object.
     * @param dataPoint - Received from the back end.
     * @private
     * @static
     */
    private static dataPointTransform(dataPoint: IMetricsData): ISeriesDataPoint {
        const res = {
            timestamp: +moment(dataPoint.timestamp),
            value: 'value_str' in dataPoint ? dataPoint.value_str : +dataPoint.value,
        };

        return res;
    }

    /**
     * Data transformer for anomalies data point object.
     * @param dataPoint - Received from the back end.
     */
    private static anomalyDataPointTransform(dataPoint: IMetricsData): ISeriesDataPoint {
        return {
            timestamp: +moment(dataPoint.timestamp),
            value: +dataPoint.value,
        };
    }

    /**
     * Return an object with two moment objects defining start and end of a processed period.
     * Basically we take current time (floored by step) and add step multiplied by limit.
     * @param step - Period of time used for data aggregation (i.e. one point every 30 s).
     * @param limit - How many data points are we expected to render.
     */
    private static getProcessedTimeFrame(step: number, limit: number): ITimeFrame {
        const currentMoment = +moment().unix();
        const end = moment.unix(currentMoment - currentMoment % step); // in seconds

        // start is the end of the first period, that is why we have "limit - 1" there
        const start = end.clone().subtract(step * (limit - 1), 's');

        return {
            start,
            end,
        };
    }

    /**
     * Reduce values into a hash by the UNIX timestamp.
     */
    private static getValuesHash(values: ISeriesDataPoint[]): Record<number, ISeriesDataPoint> {
        const hash: Record<number, ISeriesDataPoint> = {};

        values.forEach(dataPoint => hash[dataPoint.timestamp] = dataPoint);

        return hash;
    }

    /**
     * Return series id. Usually set through constructor params by Metric using this Series.
     */
    public getId(): string {
        if (this.id) {
            return this.id;
        }

        const {
            seriesId,
            itemId,
            objId,
            dimensionId,
        } = this;

        let id = `${itemId}:${seriesId}`;

        if (objId) {
            id += `:${objId}`;
        }

        if (dimensionId) {
            id += `:${dimensionId}`;
        }

        return id;
    }

    /**
     * Return series data.
     */
    public getData(): ISeriesData {
        const {
            values,
            header,
            valuesHash,
            statistics,
        } = this;

        return {
            values,
            header,
            valuesHash,
            statistics,
        };
    }

    /**
     * Return data point by UNIX timestamp.
     */
    public getDataPoint(timestamp: number): ISeriesDataPoint {
        return this.valuesHash[timestamp] || null;
    }

    /**
     * Processes the data we've got from the backend. Takes care of incremental updates and
     * consistency of data points (number of points and it's order).
     * @param rsp - Set of responses for all {@link Metric} Series.
     * @param anomalies - Set of responses for all {@link Metric} anomalies.
     * @param step -  Difference in seconds between two data points.
     * @param limit - How many points do we need for the series.
     */
    public process(
        rsp: TMetricsDataApiResponse,
        anomalies: TMetricsDataApiResponse,
        step: number,
        limit: number,
    ): boolean {
        const { params } = this;

        let gotUpdated = false;

        if (step !== params.step || limit !== params.limit) {
            this.emptyData();
            params.step = step;
            params.limit = limit;
            gotUpdated = true;
        }

        const series = this.findSeriesInResponse(rsp);

        if (series) {
            if (!this.hasData()) {
                this.setHeader(series.header);
            }

            gotUpdated = this.updateValues(series.data);

            if (gotUpdated) {
                this.updateHeader();

                // eslint-disable-next-line no-extra-parens
                if (Array.isArray((series as any).dominators)) {
                    // eslint-disable-next-line no-extra-parens
                    this.addDominators((series as any).dominators);
                }
            }
        } else if (this.hasData()) {
            // no updates came from the backend, let's generate some fake points
            gotUpdated = this.updateValues([]);
        }

        const anomaliesGotUpdated = this.updateAnomalies(
            this.findAnomaliesInResponse(anomalies),
        );

        if (gotUpdated) {
            if (!this.isAggregated()) {
                this.setValuesStatistics();
            }

            this.trigger(Series.VALUES_UPDATE_EVENT, this);
        }

        if (anomaliesGotUpdated) {
            this.trigger(Series.ANOMALIES_UPDATE_EVENT, this);
        }

        return gotUpdated || anomaliesGotUpdated;
    }

    /**
     * Add dominant contributors to dominators array.
     * @param dominators - ISO8601 values.
     */
    public addDominators(dominators: string[] = []): void {
        this.dominators = this.dominators.concat(dominators);

        if (this.dominators.length) {
            const { timestamp: minTS } = this.getFirstPoint();
            const { timestamp: maxTS } = this.getLatestPoint();
            // prevent duplicate values
            const tsHash: Record<number, any> = {};
            let hasHash = false;
            let dts = 0;

            // remove all dominant contributors not in range of data timestamps
            this.dominators = this.dominators.filter(isoTime => {
                dts = new Date(isoTime).getTime();
                hasHash = dts in tsHash;
                tsHash[dts] = undefined;

                return !hasHash && dts >= minTS && dts <= maxTS;
            });
        }
    }

    /**
     * Return item ref when set.
     */
    public getItemRef(): string {
        return this.itemRef;
    }

    /**
     * Return item id when set.
     */
    public getItemId(): string {
        return this.itemId;
    }

    /**
     * Return the first dataPoint of this series.
     */
    public getFirstPoint(): ISeriesDataPoint {
        if (this.hasData()) {
            return this.values[0];
        }

        return null;
    }

    /**
     * Return the latest data point or null if there are none.
     * @param realPoint - When true, method ignores fake points.
     */
    public getLatestPoint(realPoint = false): ISeriesDataPoint {
        if (this.hasData()) {
            const { values } = this;

            if (!realPoint) {
                return values.slice(-1)[0];
            } else {
                const index = findLastIndex(values, ({ noData }) => !noData);

                return index !== -1 ? values[index] : null;
            }
        }

        return null;
    }

    /**
     * Look for the latest point's date and returns UNIX timestamp if found or undefined if
     * not.
     * @param realPoint - When set to true only real (non faked) points will be
     */
    public getLatestPointTime(realPoint = false): number | undefined {
        const dataPoint = this.getLatestPoint(realPoint);

        return dataPoint ? dataPoint.timestamp : undefined;
    }

    /**
     * Return a series value according to the desired value type and timestamp (for EXACT type
     * only).
     */
    public getValue(
        type: MetricsDisplayValueType,
        timestamp?: Moment | number,
    ): number | string | ISeriesDataPoint | undefined {
        const { statistics, valuesHash } = this;

        timestamp = +timestamp;

        if (type && this.hasData()) {
            if (type in statistics) {
                return type !== MetricsDisplayValueType.CURRENT ?
                    statistics[type as keyof ISeriesStats] :
                    this.getLatestPoint().value;
            } else if (
                type === MetricsDisplayValueType.EXACT && timestamp && timestamp in valuesHash
            ) {
                return valuesHash[timestamp].value;
            }
        }
    }

    /**
     * Return a series value type provided by the back-end.
     */
    public getUnits(): MetricUnits | '' {
        return this.header.units || '';
    }

    /**
     * Some metrics provide string values, this is the method to figure whether we are
     * working with string value type series.
     */
    public isStringType(): boolean {
        return this.getUnits() === MetricUnits.STRING;
    }

    /**
     * To check whether series has data.
     */
    public hasData(): boolean {
        return !!this.values.length;
    }

    public getHeader(): ISeriesHeader {
        return this.header || null;
    }

    public getStats(): ISeriesStats {
        return this.statistics;
    }

    /**
     * Empty all data present in instance.
     */
    public emptyData(): void {
        this.values.length = 0;
        this.valuesHash = {};
        this.anomalies.length = 0;
        this.anomaliesQ = 0;
        this.anomaliesHash = {};
        this.dominators.length = 0;

        if (!this.isAggregated()) {
            this.setValuesStatistics();
        }
    }

    /**
     * Return human readable series label.
     */
    public getTitle(): string {
        return this.title || this.hasData() && this.getHeader().title || this.getSeriesId();
    }

    /**
     * Return true for aggregated series.
     */
    public isAggregated(): boolean {
        return this.isAggregatedSeries;
    }

    /**
     * Every series has a series id, which is called as metric_id while making API call. Not
     * to be confused with Series.id which is "full" id containing seriesId as well as other
     * ids.
     */
    public getSeriesId(): string {
        return this.seriesId;
    }

    /**
     * Return objId this series is using.
     */
    public getObjectId(): string {
        return this.objId;
    }

    /**
     * Return dimensionId this Series has.
     */
    public getDimensionId(): string {
        return this.dimensionId;
    }

    /**
     * Return color class name for charting directives.
     */
    public getColorClassName(): string {
        return this.colorClassName;
    }

    /**
     *
     */
    public getAnomaliesQ(): number {
        return this.anomaliesQ;
    }

    /**
     * Return Series.params.
     */
    public getParams(): ISeriesParams {
        return cloneDeep(this.params);
    }

    /**
     * Find series in an array of series provided by the back-end and pre filtered by
     * @param rsp - Array of series or a single one.
     */
    protected findSeriesInResponse(
        rsp: IMetricsDataSeries | IMetricsDataSeries[],
    ): IMetricsDataSeries | undefined {
        let sRsp = null;

        if (Array.isArray(rsp)) {
            const filteredSeries =
                filter(rsp, ({ header }) => this.checkHeaderForMatch(header));

            if (filteredSeries) {
                [sRsp] = filteredSeries;

                if (filteredSeries.length > 1) {
                    console.error(
                        'Series filter has returned multiple series',
                        this.seriesId,
                        rsp,
                        filteredSeries,
                    );
                }
            }
        } else if (Series.isSeriesDataObj(rsp) && this.checkHeaderForMatch(rsp.header)) {
            sRsp = rsp;
        }

        // for incremental updates of non-static-series-list Metrics some series don't get
        // updates and it's fine
        if (rsp && !sRsp && !this.hasData() && process.env.NODE_ENV !== 'production') {
            console.warn('Series didn\'t find its response', this.getId());
        }

        return sRsp;
    }

    /**
     * Find an anomaly response for the series out of the list received from the back-end and
     * pre-filtered by {@link Metric.filterResponse}.
     * @param rsp - Array of anomalies.
     * //TODO use one method for both?
     */
    protected findAnomaliesInResponse(
        rsp: IMetricsDataSeries | IMetricsDataSeries[],
    ): IMetricsDataSeries {
        let aRsp: IMetricsDataSeries;

        if (Array.isArray(rsp)) {
            aRsp = find(rsp,
                ({ header }) => this.checkHeaderForMatch(header));
        } else if (Series.isSeriesDataObj(rsp) && this.checkHeaderForMatch(rsp.header)) {
            aRsp = rsp;
        }

        return aRsp || null;
    }

    /**
     * Copy series header received from the back-end.
     */
    protected setHeader(header: ISeriesHeader): void {
        this.header = cloneDeep(header);

        this.setItemRef();

        if (this.isStringType()) {
            this.header.stringValuesList = [''];

            if (this.header.metric_description) {
                this.header.stringValuesList.push(
                    ...this.header.metric_description.split(', '),
                );
            }
        }
    }

    /**
     * Update series header metadata about an Item this data is about.
     */
    private setItemRef(): void {
        const { header } = this;

        if ('entity_ref' in header) {
            this.itemRef = header.entity_ref as string;
            this.itemId = this.stringService.slug(this.itemRef);
        } else if ('entity_uuid' in header) {
            this.itemRef = header.entity_uuid;
            this.itemId = header.entity_uuid;
        } else {
            this.itemRef = '';
            this.itemId = '';
        }

        if ('obj_id_type' in header) {
            this.itemType = header.obj_id_type;
        } else {
            this.itemType = '';
        }
    }

    /**
     * Return true when passed header object is considered to be of this series.
     */
    // TODO add pool_uuid support, now this is a workaround
    private checkHeaderForMatch(header: ISeriesHeader): boolean {
        const { objId, itemId, dimensionId } = this;
        const {
            obj_id: hObjId,
            entity_ref: hEntityRef,
            entity_uuid: hEntityId,
            pool_ref: hPoolRef,
            dimension_data: dimensionData,
        } = header;
        const hPoolId = hPoolRef ? this.stringService.slug(hPoolRef) : '';
        const hItemId = hEntityId || (hEntityRef ? this.stringService.slug(hEntityRef) : '');

        let hDimensionId = '';

        if (dimensionData) {
            hDimensionId = dimensionData[0].dimension_id;
        }

        return this.seriesId === header.name &&
            (!itemId || itemId === hItemId || itemId === hPoolId) &&
            (!objId || objId === hObjId) &&
            (!dimensionId || dimensionId === hDimensionId);
    }

    /**
     * Update (when applicable) series header after getting and setting new values.
     */
    private updateHeader(): void {
        if (this.isStringType()) {
            const { header } = this;
            const metricDescription = header.metric_description;

            header.stringValuesList.length = 1;

            if (metricDescription) {
                header.stringValuesList.push(...metricDescription.split(', '));
            } else {
                const stringValuesHash: Record<string | number, boolean> = {};

                this.values.forEach(({ value }) => stringValuesHash[value] = true);
                header.stringValuesList.push(...Object.keys(stringValuesHash));
            }
        }
    }

    /**
     * Return a dummy data point to keep a persistent values array even when back end has
     * no data for some reason.
     * @param timestamp - UNIX timestamp (in ms).
     */
    private getNoDataDataPoint(timestamp: number): ISeriesDataPoint {
        return {
            timestamp,
            value: this.isStringType() ? '' : 0,
            noData: true,
        };
    }

    /**
     * Merges server response into existent list of points. Makes appropriate shift
     * (drops some old point(s), appends new one(s)) based on the current time.
     * Return true when values list actually got updated.
     */
    private updateValues(newValuesList: IMetricsData[] = []): boolean {
        const { valuesHash: prevValuesHash, values } = this;
        const { step, limit } = this.params;
        const mergedValuesList = [];
        const stepMs = step * 1000;

        const newDataPoints = newValuesList
            .filter(({ is_null: isNull }) => !isNull)
            .map(Series.dataPointTransform);

        const newValuesHash = Series.getValuesHash(newDataPoints);

        /** @type {boolean} */
        let gotUpdated = false;

        const { start, end } = Series.getProcessedTimeFrame(step, limit);
        let startTs = Number(start);
        let endTs = Number(end);

        /** @type {boolean} */
        const noMostRecentPointData = !(endTs in newValuesHash);

        // Since we need to preserve the length of values list these are options when
        // most recent point didn't come from the backend:
        // - if we had full list of points before the update we can effectively do nothing
        //   and wait for the next update
        // - otherwise we need to add a "noData" point for the most recent timestamp
        //   and have a dip on the chart.
        if (noMostRecentPointData &&
            (startTs - stepMs in prevValuesHash || startTs - stepMs in newValuesHash)
        ) {
            // shifting time frame into the past by one step
            startTs -= stepMs;
            endTs -= stepMs;
        }

        for (let ts = startTs; ts <= endTs; ts += stepMs) {
            let dataPoint;

            if (ts in newValuesHash) {
                const newDataPoint = newValuesHash[ts];
                const hadThisPoint = ts in prevValuesHash;
                const pointIsUpdated = !hadThisPoint ||
                    prevValuesHash[ts].noData === true ||
                    prevValuesHash[ts].value !== newDataPoint.value;

                dataPoint = pointIsUpdated ? { ...newDataPoint } : prevValuesHash[ts];
                gotUpdated = gotUpdated || pointIsUpdated;
            } else if (ts in prevValuesHash) {
                dataPoint = prevValuesHash[ts];
            } else {
                gotUpdated = true;
                dataPoint = this.getNoDataDataPoint(ts);
            }

            mergedValuesList.push(dataPoint);
        }

        if (gotUpdated) {
            values.length = 0;
            values.push(...mergedValuesList);
            this.valuesHash = Series.getValuesHash(values);
        }

        return gotUpdated;
    }

    /**
     * Merged list of anomalies received from the back end into existent list.
     * Return rue when list got updated.
     * TODO if we always use same step and limit for values and anomalies, should we merge
     * anomalies Q into values?
     */
    private updateAnomalies(anomSeriesData?: IMetricsDataSeries): boolean {
        const {
            anomaliesHash: prevAnomaliesHash,
            anomalies,
        } = this;
        const mergedAnomaliesList = [];

        const dataPoints = anomSeriesData ? anomSeriesData.data : [];

        const newAnomaliesList = dataPoints
            .filter(({ value }) => value)
            .map(Series.anomalyDataPointTransform);

        const newAnomaliesHash = Series.getValuesHash(newAnomaliesList);

        let gotUpdated = false;
        let anomaliesQ = 0;

        const { step, limit } = this.params;
        const { start, end } = Series.getProcessedTimeFrame(step, limit);
        const endTs = +end;

        for (let ts = +start; ts <= endTs; ts += step * 1000) {
            let dataPoint: ISeriesDataPoint;

            if (ts in newAnomaliesHash) {
                gotUpdated = gotUpdated || !(ts in prevAnomaliesHash);
                dataPoint = newAnomaliesHash[ts];
            } else if (ts in prevAnomaliesHash) {
                dataPoint = prevAnomaliesHash[ts];
            }

            if (dataPoint) {
                mergedAnomaliesList.push(dataPoint);
                anomaliesQ += +dataPoint.value;
            }
        }

        // if we've dropped at least one point because of time frame shift
        gotUpdated = gotUpdated || mergedAnomaliesList.length !== anomalies.length;

        if (gotUpdated) {
            anomalies.length = 0;
            anomalies.push(...mergedAnomaliesList);
            this.anomaliesQ = anomaliesQ;
            this.anomaliesHash = Series.getValuesHash(anomalies);
        }

        return gotUpdated;
    }

    /**
     * Set statistics object of Series instance. Called after processing values has been
     * done and those were actually updated.
     */
    private setValuesStatistics(): void {
        const stat: ISeriesStats = {};

        let max = -Infinity;
        let maxTimestamp = null;
        let sum = 0;
        let countReal = 0;

        if (!this.isStringType()) {
            this.values.forEach(({ timestamp, value, noData }) => {
                if (!noData) {
                    const valueNum = +value;

                    if (valueNum > max) {
                        max = valueNum;
                        maxTimestamp = timestamp;
                    }

                    sum += valueNum;
                    countReal++;
                }
            });

            const { metrics_sum_agg_invalid: sumAggInvalid } = this.getHeader();

            // need fallback values for situations when all values were faked
            stat.max = isFinite(max) ? max : 0;
            stat.avg = countReal ? sum / countReal : 0;
            stat.sum = sumAggInvalid ? NaN : sum;
            stat.maxTimestamp = maxTimestamp || this.getLatestPointTime();

            if (!isNaN(+stat.sum) && this.getUnits().lastIndexOf('PER_SECOND') !== -1) {
                stat.sum *= this.params.step;
            }
        }

        // DEPRECATED, use getLatestPoint instead
        stat.current = this.getLatestPoint();

        this.statistics = stat;
    }
}

Series.ajsDependencies = [
    'NamesHelper',
    STRING_SERVICE_TOKEN,
];
